import { ISortDescriptor, ITagManagementCommand } from '@ViewModels';
import equal from 'fast-deep-equal';
import { parse } from '@AppModels/QueryStringUtils';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebounceCallback } from '../../../../../hooks/useDebounceValue';
import { ILocationState } from '../../../../../models';
import { queryStringParamsToSearchRequest } from '../../../../../models/UiUtils';
import { DefaultTagsFilter, DefaultTagsSort, getTagSearchFromCriteria, ITagsSearchFilter } from '../TagsUtilities';

interface TagsSearchContext {
	initialSearchValue: string;
	searchTerm: string;
	debouncedSetSearchTerm: (term: string) => void;
	sort: ISortDescriptor;
	setSort: (sort: ISortDescriptor) => void;
	filter: ITagsSearchFilter;
	setFilter: (filter: ITagsSearchFilter) => void;
}

const TagsSearchContext = createContext<TagsSearchContext>({
	initialSearchValue: '',
	searchTerm: '',
	debouncedSetSearchTerm: () => {
		throw new Error('Function not implemented');
	},
	sort: DefaultTagsSort,
	setSort: () => {
		throw new Error('Function not implemented');
	},
	filter: DefaultTagsFilter,
	setFilter: () => {
		throw new Error('Function not implemented');
	},
});

export const TagsSearchContextProvider = ({ children }: { children: React.ReactNode }) => {
	const location = useLocation<ILocationState<any, ITagManagementCommand>>();
	// Load search from query
	const params = parse(location?.search);
	const tagsQueryParams = queryStringParamsToSearchRequest<ITagsSearchFilter>(params);
	const queryFilter: ITagsSearchFilter = tagsQueryParams?.searchRequest || DefaultTagsFilter;
	const querySort = tagsQueryParams?.sortDescriptor || DefaultTagsSort;

	const [filter, setFilter] = useState<ITagsSearchFilter>(queryFilter);
	const [sort, setSort] = useState<ISortDescriptor>(querySort);
	const [searchTerm, setSearchTerm] = useState<string>('');

	const initialSearchValue = useRef(queryFilter?.criteria ? getTagSearchFromCriteria(queryFilter.criteria) : '');

	useEffect(() => {
		if (!equal(filter, queryFilter)) {
			setFilter(queryFilter);
		}
		if (!equal(sort, querySort)) {
			setSort(querySort);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	return (
		<TagsSearchContext.Provider
			value={{
				initialSearchValue: initialSearchValue.current,
				searchTerm,
				debouncedSetSearchTerm: useDebounceCallback(setSearchTerm),
				sort,
				setSort,
				filter,
				setFilter,
			}}
		>
			{children}
		</TagsSearchContext.Provider>
	);
};

export const useTagsSearchContext = () => {
	return useContext<TagsSearchContext>(TagsSearchContext);
};
